import React, { useMemo } from 'react';
import { useGameStore } from '../../state/gameStore';
import type { BnBNode } from '../../../game/bnb-manager';
import { appBus } from '../../../app/app-events';

// --- Configuration ---
const NODE_WIDTH = 70;
const NODE_HEIGHT = 40;
const LEVEL_HEIGHT = 100;
const MIN_NODE_GAP = 30;

interface TreeLayout {
  nodes: Map<string, { x: number; y: number; width: number }>;
  width: number;
  height: number;
}

// --- Layout Algorithm ---
const calculateTreeLayout = (
  rootId: string,
  nodes: Map<string, BnBNode>
): TreeLayout => {
  const layout = new Map<string, { x: number; y: number; width: number }>();

  // Helper: Get subtree width
  const getSubtreeWidth = (nodeId: string): number => {
    const node = nodes.get(nodeId);
    if (!node) return 0;

    if (node.children.length === 0) {
      const w = NODE_WIDTH;
      layout.set(nodeId, { x: 0, y: 0, width: w });
      return w;
    }

    let totalWidth = 0;
    node.children.forEach((childId, index) => {
      if (index > 0) totalWidth += MIN_NODE_GAP;
      totalWidth += getSubtreeWidth(childId);
    });

    layout.set(nodeId, { x: 0, y: 0, width: totalWidth });
    return totalWidth;
  };

  const totalTreeWidth = getSubtreeWidth(rootId);
  let maxDepth = 0;

  // Helper: Assign coordinates
  const assignCoordinates = (nodeId: string, startX: number, depth: number) => {
    const node = nodes.get(nodeId);
    if (!node) return;

    const subtreeW = layout.get(nodeId)?.width || NODE_WIDTH;
    const myX = startX + subtreeW / 2;
    const myY = depth * LEVEL_HEIGHT + NODE_HEIGHT + 20;

    // Update layout with final X, Y
    const existing = layout.get(nodeId);
    if (existing) {
      existing.x = myX;
      existing.y = myY;
    } else {
      layout.set(nodeId, { x: myX, y: myY, width: subtreeW });
    }

    maxDepth = Math.max(maxDepth, depth);

    let currentX = startX;
    node.children.forEach(childId => {
      const childW = layout.get(childId)?.width || NODE_WIDTH;
      assignCoordinates(childId, currentX, depth + 1);
      currentX += childW + MIN_NODE_GAP;
    });
  };

  assignCoordinates(rootId, 0, 0);

  return {
    nodes: layout,
    width: totalTreeWidth,
    height: (maxDepth + 1) * LEVEL_HEIGHT + 60
  };
};

// --- Icons ---
const IncumbentIcon: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <filter id="glow-gold" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
      <feMerge>
        <feMergeNode in="coloredBlur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    {/* Diamond Shape for "Current Best" */}
    <path d="M0 -8 L8 0 L0 8 L-8 0 Z" fill="url(#grad-incumbent)" stroke="#fff" strokeWidth="1.5" filter="url(#drop-shadow)" />
  </g>
);

const OptimalIcon: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    {/* Crown Shape for "Optimal" */}
    <path d="M-8 5 L-8 -3 L-4 1 L0 -8 L4 1 L8 -3 L8 5 Z" fill="url(#grad-optimal)" stroke="#fff" strokeWidth="1.5" filter="url(#drop-shadow)" />
  </g>
);

const NodeSVG: React.FC<{
  node: BnBNode;
  x: number;
  y: number;
  isActive: boolean;
  isIncumbent: boolean;
  isGlobalOptimal: boolean;
  onSelect: (id: string) => void;
}> = ({ node, x, y, isActive, isIncumbent, isGlobalOptimal, onSelect }) => {

  const isIntegral = node.status === 'fathomed';
  const isPruned = node.status === 'pruned';

  const score = node.lp?.optimalValue !== undefined
    ? (Number.isFinite(node.lp.optimalValue) ? node.lp.optimalValue.toFixed(2) : '-inf')
    : '?';

  let fillUrl = "url(#grad-default)";
  let stroke = "#475569";
  let textColor = "#e2e8f0";
  let strokeWidth = 1;

  if (isActive) {
    stroke = "#38bdf8";
    fillUrl = "url(#grad-active)";
    strokeWidth = 2;
    textColor = "#ffffff";
  } else if (isIntegral) {
    stroke = "#10b981";
    fillUrl = "url(#grad-integral)";
    textColor = "#d1fae5";
  } else if (isPruned) {
    stroke = "#ef4444";
    fillUrl = "url(#grad-pruned)";
    textColor = "#fca5a5";
  }

  if (isIncumbent) {
    stroke = "#f59e0b";
    strokeWidth = 2;
  }

  return (
    <g
      transform={`translate(${x - NODE_WIDTH / 2}, ${y - NODE_HEIGHT / 2})`}
      onClick={(e) => { e.stopPropagation(); onSelect(node.id); }}
      style={{ cursor: 'pointer' }}
      className="group"
    >
      <rect
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx="12"
        fill={fillUrl}
        stroke={stroke}
        strokeWidth={strokeWidth}
        filter="url(#drop-shadow)"
        className="transition-all duration-300"
      />

      <text
        x={NODE_WIDTH / 2}
        y={NODE_HEIGHT / 2 + 5}
        textAnchor="middle"
        fill={textColor}
        fontSize="13"
        fontWeight="700"
        fontFamily="monospace"
        className="pointer-events-none select-none drop-shadow-sm"
      >
        {score}
      </text>

      {isPruned && (
        <g stroke="#ef4444" strokeWidth="2" opacity="0.6">
          <line x1={15} y1={12} x2={NODE_WIDTH - 15} y2={NODE_HEIGHT - 12} />
          <line x1={NODE_WIDTH - 15} y1={12} x2={15} y2={NODE_HEIGHT - 12} />
        </g>
      )}

      {isIncumbent && !isGlobalOptimal && (
        <IncumbentIcon x={NODE_WIDTH} y={0} />
      )}

      {isIncumbent && isGlobalOptimal && (
        <OptimalIcon x={NODE_WIDTH} y={0} />
      )}
    </g>
  );
};

const EdgeSVG: React.FC<{ x1: number; y1: number; x2: number; y2: number; label?: string }> = ({ x1, y1, x2, y2, label }) => {
  // Place label closer to the child node with a small perpendicular offset
  const t = 0.6; // moderate bias toward the child end
  const px = x1 + (x2 - x1) * t;
  const py = y1 + (y2 - y1) * t;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const side = x2 >= x1 ? 1 : -1; // push labels away from center line in opposite directions
  const offset = 8;
  const labelX = px + nx * offset * side;
  const labelY = py + ny * offset * side - 2; // slight upward tweak

  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#475569"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.6"
      />
      {label && (
        <g transform={`translate(${labelX}, ${labelY})`}>
          <rect
            x={-18}
            y={-10}
            width={36}
            height={20}
            rx={6}
            fill="#0f172a"
            stroke="#334155"
            strokeWidth="1"
          />
          <text
            x={0}
            y={4}
            textAnchor="middle"
            fill="#94a3b8"
            fontSize="10"
            fontWeight="600"
            fontFamily="monospace"
          >
            {label}
          </text>
        </g>
      )}
    </g>
  );
};

const BnBTree: React.FC = () => {
  const { rootNodeId, nodes, currentNodeId, incumbentNodeId, commandLocked } = useGameStore(state => state.ui.bnbState);

  // Check if algorithm is finished (no open nodes)
  const isGlobalOptimal = useMemo(() => {
    let hasOpen = false;
    for (const n of nodes.values()) {
      if (n.status === 'open') {
        hasOpen = true;
        break;
      }
    }
    return !hasOpen;
  }, [nodes]);

  const treeData = useMemo(() => {
    if (!rootNodeId || nodes.size === 0) return null;
    return calculateTreeLayout(rootNodeId, nodes);
  }, [rootNodeId, nodes.size, nodes]);

  if (!treeData || !rootNodeId) {
    return (
      <div className="flex items-center justify-center h-full opacity-30">
        <span className="text-2xl grayscale">🌳</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto custom-scrollbar relative cursor-grab active:cursor-grabbing flex justify-center">
      <div style={{ width: Math.max(treeData.width + 100, 200), height: Math.max(treeData.height, 200) }} className="relative flex-shrink-0">
        <svg
          width="100%"
          height="100%"
          style={{ minWidth: treeData.width + 100, minHeight: treeData.height }}
        >
          <defs>
            {/* Gradients */}
            <linearGradient id="grad-default" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="grad-active" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0ea5e9" />
              <stop offset="100%" stopColor="#0284c7" />
            </linearGradient>
            <linearGradient id="grad-integral" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="grad-pruned" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="100%" stopColor="#b91c1c" />
            </linearGradient>
            <linearGradient id="grad-incumbent" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <linearGradient id="grad-optimal" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fcd34d" />
              <stop offset="100%" stopColor="#b45309" />
            </linearGradient>

            {/* Shadows */}
            <filter id="drop-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
              <feOffset dx="0" dy="2" result="offsetblur" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {Array.from(treeData.nodes.entries()).map(([id, pos]) => {
            const node = nodes.get(id);
            if (!node) return null;
            return node.children.map(childId => {
              const childPos = treeData.nodes.get(childId);
              const childNode = nodes.get(childId);
              if (!childPos || !childNode) return null;

              const lastBound = childNode.bounds[childNode.bounds.length - 1];
              const label = lastBound
                ? `${['x', 'y', 'z'][lastBound.varIndex]}${lastBound.type === 'le' ? '≤' : '≥'}${lastBound.value}`
                : '';

              return (
                <EdgeSVG
                  key={`${id}-${childId}`}
                  x1={pos.x} y1={pos.y + NODE_HEIGHT / 2}
                  x2={childPos.x} y2={childPos.y - NODE_HEIGHT / 2}
                  label={label}
                />
              );
            });
          })}

          {Array.from(treeData.nodes.entries()).map(([id, pos]) => {
            const node = nodes.get(id);
            if (!node) return null;
            return (
              <NodeSVG
                key={id}
                node={node}
                x={pos.x}
                y={pos.y}
                isActive={currentNodeId === id}
                isIncumbent={incumbentNodeId === id}
                isGlobalOptimal={isGlobalOptimal}
                onSelect={(id) => {
                  if (!commandLocked) {
                    appBus.emit('bnb-select-requested', { nodeId: id });
                  }
                }}
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default BnBTree;
